import { useMemo, useState } from 'react';
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnSizingState,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table';
import { useSnackbar } from 'notistack';
import {
  useGetAllItemsWithAllPrices,
  useExportItemsWithAllPrices,
} from '@/api/items/items.hooks';
import type { ItemWithAllPrices } from '@/api/items/items.types';
import { useGridState } from '@hooks/use-grid-state';
import { DsButton, DsAlert, DsSearchInput, DsTabNav, type DsTab } from '@/modules/internal-v2/ds';
import { DsTable } from '../shared/ds-table.component';
import {
  createSearchPattern,
  parseSearchTerms,
  matchesFields,
} from '@/modules/internal-v2/shared/search-parser';
import { buildItemsColumns } from './components/items-columns';
import { SpecEditModal } from './components/spec-edit-modal.component';
import { BulkSpecEditModal } from './components/bulk-spec-edit-modal.component';
import './items.css';

const SEARCH_FIELDS: (keyof ItemWithAllPrices)[] = [
  'id', 'name', 'manufacturer', 'itemGroup', 'barCode',
];

const TABS: DsTab[] = [
  { id: 'artikel', label: 'Artikelliste' },
];

const EMPTY_SIZING: ColumnSizingState = {};

export function InternalItemsPage() {
  const { getCustom, setCustom } = useGridState('items');
  const searchTerm = getCustom<string>('searchTerm', '');
  const setSearchTerm = (v: string) => setCustom('searchTerm', v);
  const showNeededOnly = getCustom<boolean>('showNeededOnly', false);
  const setShowNeededOnly = (v: boolean) => setCustom('showNeededOnly', v);
  const columnSizing = getCustom<ColumnSizingState>('columnSizing', EMPTY_SIZING);
  const setColumnSizing = (
    updater: ColumnSizingState | ((prev: ColumnSizingState) => ColumnSizingState),
  ) =>
    setCustom(
      'columnSizing',
      typeof updater === 'function' ? updater(columnSizing) : updater,
    );
  const [activeTab, setActiveTab] = useState('artikel');

  const { data: itemsData, isPending: itemsQueryIsPending } =
    useGetAllItemsWithAllPrices();

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'itemGroup', desc: false },
    { id: 'quantityOnStock', desc: true },
  ]);
  const [editing, setEditing] = useState<ItemWithAllPrices | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [bulkOpen, setBulkOpen] = useState(false);

  const columns = useMemo(
    () => buildItemsColumns({ onEditSpecs: setEditing }),
    [],
  );

  const filteredItems = useMemo(() => {
    if (!itemsData) return [];

    const searchGroups = parseSearchTerms(searchTerm).map(({ terms }) => ({
      patterns: terms.map((term) => new RegExp(createSearchPattern(term.toLowerCase()))),
    }));

    return itemsData.filter((item) => {
      if (showNeededOnly) {
        const total =
          item.quantityOnStock -
          item.quantityOrderedByCustomers +
          item.quantityOrderedFromVendors;
        if (item.minInventory <= total) return false;
      }

      return searchGroups.every(({ patterns }) =>
        patterns.every((regex) => matchesFields(item, regex, SEARCH_FIELDS)),
      );
    });
  }, [itemsData, searchTerm, showNeededOnly]);

  const tabsWithCounts = useMemo<DsTab[]>(() =>
    TABS.map((t) => t.id === 'artikel' ? { ...t, count: filteredItems.length } : t),
    [filteredItems.length],
  );

  const table = useReactTable({
    data: filteredItems,
    columns,
    state: { sorting, rowSelection, columnSizing },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnSizingChange: setColumnSizing,
    enableRowSelection: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    enableSortingRemoval: false,
  });

  const selectedItems = useMemo(
    () =>
      table
        .getSelectedRowModel()
        .rows.map((r) => r.original as ItemWithAllPrices),
    // react-table mutates the selection model in-place; depend on the keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowSelection, filteredItems],
  );

  const { enqueueSnackbar } = useSnackbar();
  const exportItems = useExportItemsWithAllPrices({
    onError: () =>
      enqueueSnackbar('Export fehlgeschlagen.', { variant: 'error' }),
  });

  const handleExport = (format: 'xlsx' | 'csv') => {
    if (selectedItems.length === 0) return;
    exportItems.mutate({ itemIds: selectedItems.map((i) => i.id), format });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: 'var(--surface)' }}>
      <DsTabNav tabs={tabsWithCounts} activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'artikel' && (
        <>
          <div className="pt-toolbar">
            <DsSearchInput
              className="pt-search"
              value={searchTerm}
              onSearch={setSearchTerm}
              debounce={0}
              placeholder="watch apple 2025 OR samsung tablet"
              autoFocus
            />
            {showNeededOnly && (
              <DsAlert severity="error">Filter aktiv: Nur Artikel mit Bedarf</DsAlert>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
              <DsButton
                variant={showNeededOnly ? 'primary' : 'default'}
                onClick={() => setShowNeededOnly(!showNeededOnly)}
              >
                {showNeededOnly ? 'Alle anzeigen' : 'Nur Bedarf anzeigen'}
              </DsButton>
            </div>
          </div>
          <DsTable
            table={table}
            fixedLayout
            enableColumnResizing
            emptyMessage={itemsQueryIsPending ? 'Laden…' : 'Keine Artikel gefunden.'}
          />
          {selectedItems.length > 0 && (
            <div className="items-selection-bar">
              <span className="items-selection-count">
                {selectedItems.length} ausgewählt
              </span>
              <DsButton variant="ghost" onClick={() => setRowSelection({})}>
                Auswahl aufheben
              </DsButton>
              <div style={{ flex: 1 }} />
              <DsButton
                variant="default"
                disabled={exportItems.isPending}
                onClick={() => handleExport('xlsx')}
              >
                Export .xlsx
              </DsButton>
              <DsButton
                variant="default"
                disabled={exportItems.isPending}
                onClick={() => handleExport('csv')}
              >
                Export .csv
              </DsButton>
              <DsButton
                variant="primary"
                onClick={() => setBulkOpen(true)}
              >
                Spezifikationen bearbeiten…
              </DsButton>
            </div>
          )}
        </>
      )}

      {editing && (
        <SpecEditModal
          open
          item={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {bulkOpen && (
        <BulkSpecEditModal
          open
          items={selectedItems}
          onClose={() => setBulkOpen(false)}
          onApplied={() => {
            setBulkOpen(false);
            setRowSelection({});
          }}
        />
      )}
    </div>
  );
}
